import { useRouter } from "next/router";
import { createContext, useContext, useMemo } from "react";
import { usePrefsCtx } from "~/components/onboard/db-prefs-provider";
import { calcPriceAndRange } from "~/components/outfit-product/outfit-product-utils";
import { Mixpanel } from "~/components/tracking-provider";
import type { Currency } from "~/lib/currencies";
import type { CurrencyPrices } from "~/lib/types";
import type { BoundingBox } from "~/utils/bounding-box";

interface ContextType {
  id: string;
  outfitId: string;
  title: string;
  rect: BoundingBox;
  src: string;
  price?: number;
  currency: Currency;
  search: () => void;
}

const OutfitProductContext = createContext<ContextType | null>(null);

export function useOutfitProductCtx() {
  const ctx = useContext(OutfitProductContext);
  if (!ctx) {
    throw new Error(
      "useOutfitProductCtx must be used within a OutfitProductProvider",
    );
  }
  return ctx;
}

export interface OutfitProductBaseProps {
  id: string;
  bounding_box: BoundingBox;
  price?: number;
  similar_products?: {
    product_id: string;
    price_range: string;
    price: CurrencyPrices;
  }[];
  derived_features?: {
    clothing_type?: string | null;
    price_category?: string | null;
  } | null;
}

interface Props {
  children: React.ReactNode;
  outfit: { id: string; image_url: string };
  product: OutfitProductBaseProps;
}

export const OutfitProductProvider = ({ children, outfit, product }: Props) => {
  const router = useRouter();
  const { currency, prefs } = usePrefsCtx();
  const priceAndRange = useMemo(() => {
    return calcPriceAndRange({
      matches: product.similar_products ?? [],
      category: product.derived_features?.price_category ?? "",
      currency,
      targetRange: prefs?.price_range,
    });
  }, [product, currency, prefs]);

  const search = () => {
    Mixpanel.track("Search", {
      source: "Outfit Product",
      type: "OUTFIT_PRODUCT",
    });
    void router.push({
      pathname: "/search",
      query: {
        search_type: "OUTFIT_PRODUCT",
        query: product.id,
        outfit_id: outfit.id,
        minPrice: priceAndRange.min,
        maxPrice: priceAndRange.max,
      },
    });
  };

  return (
    <OutfitProductContext.Provider
      value={{
        id: product.id,
        outfitId: outfit.id,
        title: product?.derived_features?.clothing_type ?? "",
        rect: product.bounding_box,
        src: outfit.image_url,
        price: priceAndRange.price,
        currency,
        search,
      }}
    >
      {children}
    </OutfitProductContext.Provider>
  );
};
